import {ExpandButton} from '@github-ui/expand-button'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {GlobalCommands} from '@github-ui/ui-commands'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Button, type ButtonProps} from '@primer/react'
import type {TooltipProps} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import React from 'react'

import styles from './ExpandFileTreeButton.module.css'

export interface ExpandFileTreeButtonProps extends Pick<ButtonProps, 'variant'> {
  expanded?: boolean
  onToggleExpanded: () => void
  className?: string
  ariaControls: string
  textAreaId: string
  useFilesButtonBreakpoint?: boolean
  getTooltipDirection?: (expanded?: boolean) => TooltipProps['direction']
  ref?: React.Ref<HTMLButtonElement>
}

export const ExpandFileTreeButton = ({
  ref,
  expanded,
  onToggleExpanded,
  className,
  ariaControls,
  useFilesButtonBreakpoint = true,
  variant,
  getTooltipDirection,
}: ExpandFileTreeButtonProps) => {
  const {isServer: isSSR} = useRuntimeEnvironment()
  const tooltipDirection = getTooltipDirection?.(expanded) ?? 'se'

  return (
    <>
      {/* on the server, the expanded value will purely be whatever their saved
    setting is, which might be expanded. On mobile widths we don't ever default to
    having the tree expanded, so on the server we need to just hard code it to
    show the regular not expanded version of everything*/}
      {useFilesButtonBreakpoint && (!expanded || isSSR) && (
        <Button
          aria-label="Expand file tree"
          leadingVisual={ArrowLeftIcon}
          data-testid="expand-file-tree-button-mobile"
          ref={ref}
          onClick={onToggleExpanded}
          variant={variant ?? 'invisible'}
          className={styles.Button_1}
        >
          Files
        </Button>
      )}
      <ExpandButton
        className={clsx(className, 'position-relative', styles.expandButton, {
          [styles.filesButtonBreakpoint]: useFilesButtonBreakpoint && (!expanded || isSSR),
        })}
        expanded={expanded}
        alignment="left"
        ariaLabel={expanded ? 'Collapse file tree' : 'Expand file tree'}
        tooltipDirection={tooltipDirection}
        testid="file-tree-button"
        ariaControls={ariaControls}
        ref={ref}
        variant={variant}
        onToggleExpanded={onToggleExpanded}
      />

      <GlobalCommands commands={{'code-view:toggle-tree': () => onToggleExpanded()}} />
    </>
  )
}

ExpandFileTreeButton.displayName = 'ExpandFileTreeButton'
